import React, { useContext, useEffect, useState } from 'react'
import { CCard, CCol, CFormLabel, CFormSwitch, CRow } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilPencil } from '@coreui/icons'
import moment from 'moment'
import SearchTable from 'src/components/searchTable'
import { checkPermissions, raiseToast } from 'src/views/utils/helper'
import { GlobalContext } from '../../context/GlobalState'
import { PERMISSIONS } from '../../utils/permissions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDoubleDown } from '@fortawesome/free-solid-svg-icons'
import useAxios from 'axios-hooks'
import { AFFILIATES_API, DISCOUNT_API, SUBSCRIPTION_API } from '../../utils/urls'
import { AppContext } from '../../context/app'
import DiscountCreationModal from '../pricing/DiscountCreationModal'
import { useParams } from 'react-router-dom'
import ClipboardCopy from '../../../components/copyToClipboard'

const AffiliatesDetail = () => {
  const { affiliateId } = useParams()
  const { setLoader, accountObj } = useContext(AppContext)
  const { userSessionObj } = useContext(GlobalContext)
  const [openModal, setOpenModal] = useState(false)
  const [selectedDisc, setSelectedDisc] = useState('')
  const [activeSubscription, setActiveSubscription] = useState([])
  const [affiliatesDiscount, setAffiliatesDiscount] = useState([])

  const createAllowed =
    checkPermissions(userSessionObj.session_data.permissions, PERMISSIONS.AFFILIATES.CREATE) &&
    checkPermissions(userSessionObj.session_data.permissions, PERMISSIONS.DISCOUNT.CREATE)
  const updateAllowed =
    checkPermissions(userSessionObj.session_data.permissions, PERMISSIONS.AFFILIATES.UPDATE) &&
    checkPermissions(userSessionObj.session_data.permissions, PERMISSIONS.DISCOUNT.UPDATE)

  const [{ data: affiliateDetail, loading: affiliateDetailLoading }, affiliateDetailCall] =
    useAxios({
      url: AFFILIATES_API,
      method: 'get',
      params: {
        affiliate_id: affiliateId,
      },
    })

  const [{ data: subscriptionList, loading: subscriptionListLoading }] = useAxios({
    url: SUBSCRIPTION_API,
    method: 'get',
  })

  const [{ data: createDiscount, loading: createDiscountLoading }, discountCreateCall] = useAxios(
    {
      url: DISCOUNT_API,
      method: 'post',
    },
    { manual: true },
  )

  const [{ data: updateDiscount, loading: updateDiscountLoading }, discountUpdateCall] = useAxios(
    {
      url: DISCOUNT_API,
      method: 'put',
    },
    { manual: true },
  )

  useEffect(() => {
    if (subscriptionList && subscriptionList.success) {
      let { data } = subscriptionList
      setActiveSubscription(
        data
          .filter((sub) => sub.status)
          .map((item) => ({
            ...item,
            displayName: item.name + ` (${item.type === 'normal' ? 'Subscription' : 'PPV'})`,
          })),
      )
    }
  }, [subscriptionList])

  useEffect(() => {
    if (affiliateDetail && affiliateDetail.success) {
      if (affiliateDetail.data.discounts) {
        let { discounts } = affiliateDetail.data
        let tempArr = []
        discounts.forEach((ele) => {
          let tempEle = ele
          tempEle[`valuePercent`] = `${ele.value} (${ele.value_type})`
          tempArr.push(tempEle)
        })
        setAffiliatesDiscount(tempArr)
      }
    }
  }, [affiliateDetail])

  useEffect(() => {
    setLoader(
      subscriptionListLoading ||
        createDiscountLoading ||
        updateDiscountLoading ||
        affiliateDetailLoading,
    )
  }, [
    subscriptionListLoading,
    createDiscountLoading,
    updateDiscountLoading,
    affiliateDetailLoading,
  ])

  useEffect(() => {
    affiliateDetailCall()
  }, [])

  useEffect(() => {
    if (createDiscount && createDiscount.success) {
      affiliateDetailCall()
      raiseToast('success', 'Discount created successfully')
    }
  }, [createDiscount])

  useEffect(() => {
    if (updateDiscount && updateDiscount.success) {
      affiliateDetailCall()
      raiseToast('success', 'Discount updated successfully')
    }
  }, [updateDiscount])

  const handleStatus = async (info) => {
    if (!updateAllowed) {
      raiseToast('error', "You don't have enough permission")
      return
    }
    discountUpdateCall({
      params: {
        discount_id: info._id,
      },
      data: {
        status: !info.status,
      },
    })
  }

  const handleEdit = (info) => {
    if (!updateAllowed) {
      raiseToast('error', "You don't have enough permission")
      return
    }
    setSelectedDisc(info)
    setOpenModal(true)
  }

  const handleAdd = () => {
    if (!createAllowed) {
      raiseToast('error', "You don't have enough permission")
      return
    }
    setSelectedDisc({})
    setOpenModal(!openModal)
  }

  const addDiscount = (payload) => {
    discountCreateCall({
      data: payload,
    })
    setOpenModal(false)
  }

  const editDiscount = (payload, id) => {
    discountUpdateCall({
      params: {
        discount_id: id,
      },
      data: payload,
    })
    setOpenModal(false)
  }

  const tableConfig = [
    {
      header: 'Name',
      dataCell: (info) => info.name,
      width: 20,
    },
    {
      header: 'Value/Percentage',
      dataCell: (info) => info.valuePercent,
      width: 19,
    },
    {
      header: 'Max Discount',
      dataCell: (info) => info.max_value,
      width: 19,
    },
    {
      header: 'Discount Code',
      dataCell: (info) => info.discount_code,
      width: 19,
    },
    {
      header: 'Created On',
      dataCell: (info) => moment.utc(info.created_date).format('DD-MM-YYYY'),
      width: 15,
    },
    {
      header: 'Status',

      dataCell: (info) => (
        <CFormSwitch
          label={info.status ? 'Active' : 'Disabled'}
          checked={info.status}
          valid={info.status}
          value={info.status}
          onChange={() => handleStatus(info)}
          size="lg"
        />
      ),
      width: 16,
    },
    {
      header: <FontAwesomeIcon icon={faAngleDoubleDown} size="lg" className="mx-1" />,
      dataCell: (info) => (
        <CIcon
          icon={cilPencil}
          style={{ cursor: 'pointer' }}
          size="lg"
          title="Edit"
          onClick={() => handleEdit(info)}
        />
      ),
      width: 6,
    },
  ]

  return (
    <>
      <h4>Affiliate Detail</h4>
      <p>{'Create or manage affiliates discount'}</p>
      <CCard style={{ padding: '1rem' }}>
        {affiliateDetail && affiliateDetail.data && (
          <>
            <CRow>
              <CCol lg="1">
                <CFormLabel className={'fw-bolder'}>Name :</CFormLabel>
              </CCol>
              <CCol lg="3">
                <p>{affiliateDetail.data.name}</p>
              </CCol>
              <CCol lg="1">
                <CFormLabel className={'fw-bolder'}>Email :</CFormLabel>
              </CCol>
              <CCol lg="3">
                <p>{affiliateDetail.data.email}</p>
              </CCol>
              <CCol lg="1">
                <CFormLabel className={'fw-bolder'}>Mobile :</CFormLabel>
              </CCol>
              <CCol lg="2">
                <p>{affiliateDetail.data.mobile}</p>
              </CCol>
            </CRow>
            <CRow>
              <CCol lg="2">
                <CFormLabel className={'fw-bolder'}>Affiliate Code :</CFormLabel>
              </CCol>
              <CCol lg="2">
                <p>{affiliateDetail.data.code}</p>
              </CCol>
              <CCol lg="2">
                <CFormLabel className={'fw-bolder'}>Signup Link :</CFormLabel>
              </CCol>
              <CCol lg="6">
                <ClipboardCopy
                  copyText={`${accountObj.domain_name}/signup?referral_code=${affiliateDetail.data.code}`}
                />
              </CCol>
            </CRow>
          </>
        )}
      </CCard>
      <SearchTable
        tableCardHeight={50}
        disableButton={false}
        buttonName={`+ ADD Discount`}
        buttonFunction={handleAdd}
        searchTerm={`name`}
        tableContent={affiliatesDiscount}
        tableConfig={tableConfig}
        createPermission={createAllowed}
        type={'affiliate discount'}
      />
      {openModal && (
        <DiscountCreationModal
          affiliate={affiliateId}
          openModal={openModal}
          onCancel={() => setOpenModal(false)}
          subscription={activeSubscription}
          userList={[]}
          type={'vouch'}
          data={selectedDisc}
          createCallback={addDiscount}
          updateCallback={editDiscount}
        />
      )}
    </>
  )
}

export default AffiliatesDetail
